import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle response errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (userData) => api.post('/auth/register', userData),
    getMe: () => api.get('/auth/me'),
    updateProfile: (userData) => api.put('/auth/update-profile', userData),
};

// Services API
export const servicesAPI = {
    getAll: () => api.get('/services'),
    getBySlug: (slug) => api.get(`/services/${slug}`),
    getById: (id) => api.get(`/services/${id}`),
    create: (data) => api.post('/services', data),
    update: (id, data) => api.put(`/services/${id}`, data),
    delete: (id) => api.delete(`/services/${id}`),
};

// Portfolio API
export const portfolioAPI = {
    getAll: (category) => api.get('/portfolio', { params: { category } }),
    getBySlug: (slug) => api.get(`/portfolio/${slug}`),
    getById: (id) => api.get(`/portfolio/${id}`),
    create: (data) => api.post('/portfolio', data),
    update: (id, data) => api.put(`/portfolio/${id}`, data),
    delete: (id) => api.delete(`/portfolio/${id}`),
};

// News API
export const newsAPI = {
    getAll: (params) => api.get('/news', { params }),
    getBySlug: (slug) => api.get(`/news/${slug}`),
    getById: (id) => api.get(`/news/${id}`),
    create: (data) => api.post('/news', data),
    update: (id, data) => api.put(`/news/${id}`, data),
    delete: (id) => api.delete(`/news/${id}`),
};

// Team API
export const teamAPI = {
    getAll: (department) => api.get('/team', { params: { department } }),
    getById: (id) => api.get(`/team/${id}`),
    create: (data) => api.post('/team', data),
    update: (id, data) => api.put(`/team/${id}`, data),
    delete: (id) => api.delete(`/team/${id}`),
};

// Categories API
export const categoriesAPI = {
    getAll: () => api.get('/categories'),
    getActive: () => api.get('/categories/active'),
    getById: (id) => api.get(`/categories/${id}`),
    getBySlug: (slug) => api.get(`/categories/slug/${slug}`),
    create: (data) => api.post('/categories', data),
    update: (id, data) => api.put(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`),
};

// Company API
export const companyAPI = {
    get: () => api.get('/company'),
    update: (id, data) => api.put(`/company/${id}`, data),
    create: (data) => api.post('/company', data),
};

// Upload API
export const uploadAPI = {
    single: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/single', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },
    multiple: (files) => {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('files', file);
        });
        return api.post('/upload/multiple', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },
    delete: (filename) => api.delete(`/upload/${filename}`),
};

// Vision Mission API
export const visionMissionAPI = {
    get: () => api.get('/vision-mission'),
    create: (data) => api.post('/vision-mission', data),
    update: (id, data) => api.put(`/vision-mission/${id}`, data),
};

// About API
export const aboutAPI = {
    get: () => api.get('/about'),
    create: (data) => api.post('/about', data),
    update: (id, data) => api.put(`/about/${id}`, data),
};

// Legal API
export const legalAPI = {
    getAll: () => api.get('/legal'),
    getByType: (type) => api.get(`/legal/${type}`),
    create: (data) => api.post('/legal', data),
    update: (id, data) => api.put(`/legal/${id}`, data),
    delete: (id) => api.delete(`/legal/${id}`),
};

// Contact API
export const contactAPI = {
    get: () => api.get('/contact'),
    create: (data) => api.post('/contact', data),
    update: (id, data) => api.put(`/contact/${id}`, data),
};

// Web Config API
export const webConfigAPI = {
    get: () => api.get('/config'),
    create: (data) => api.post('/config', data),
    update: (id, data) => api.put(`/config/${id}`, data),
};

export const certificationAPI = {
    getAll: () => api.get('/certifications'),
    get: (id) => api.get(`/certifications/${id}`),
    create: (data) => api.post('/certifications', data),
    update: (id, data) => api.put(`/certifications/${id}`, data),
    delete: (id) => api.delete(`/certifications/${id}`),
};

export const careerAPI = {
    getAll: () => api.get('/careers'),
    getActive: () => api.get('/careers/active'),
    get: (id) => api.get(`/careers/${id}`),
    getBySlug: (slug) => api.get(`/careers/slug/${slug}`),
    create: (data) => api.post('/careers', data),
    update: (id, data) => api.put(`/careers/${id}`, data),
    delete: (id) => api.delete(`/careers/${id}`),
};

export default api;

